/**
 * One outline relation board, built from a config.
 *
 * The live Service Plan outline and the Service Plan Template outline are the same board. Keeping
 * them as two copies is what let the template drift into a Delete-only board with a generic label
 * while the live one grew four more bulk actions, a create chord and a selection summary. Both are
 * now thin configs over this builder, so a bulk action added here appears on both or is flagged off
 * on one for a stated reason.
 */
import { computed, inject, signal, Type } from '@angular/core';
import { Entity, EntityLink, EntityViewDisplay } from '@jms/model';
import { firstValueFrom } from 'rxjs';
import { EntityDataService, EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import { SCHEDULE_TODAY } from '@workbuddy/ui/v2/fields/schedule';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { EntityBoardContext } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { EntityBoardStore } from '@workbuddy/ui/v2/blocks/entity.board/state';
import { EntityDialogs } from '@workbuddy/ui/v2/blocks/entity.dialog';
import { RelationBoardBuilder, RelationBoardMount } from '@workbuddy/ui/v2/surface.engine';
import { LazyRelationBoardHostComponent } from '@workbuddy/ui/v2/surface.engine/surfaces/manage';
import { OutlineRow } from '../outline.authoring.model';
import { openOutlineChildCreate, resolveOutlineCreateParent } from '../outline.create';
import { OutlineRevealApi, revealOutlineChild } from '../outline.reveal';
import { filterOutlineSearchRows } from '../outline.search';
import { OutlineRowRendererComponent } from '../outline.row/outline.row.renderer';
import { handleOutlineSetBlackout, OUTLINE_BLACKOUT_ICON, OUTLINE_BLACKOUT_LABEL, OUTLINE_SET_BLACKOUT_ACTION } from '../bulk/outline.bulk.blackout';
import { handleOutlineSetCrew, OUTLINE_SET_CREW_ACTION } from '../bulk/outline.bulk.crew';
import { handleOutlineDelete, OUTLINE_DELETE_ACTION } from '../bulk/outline.bulk.delete';
import { outlineBulkSelectionShape, outlineDeleteLabel, outlineSelectionSummary } from '../bulk/outline.bulk.labels';
import { handleOutlineMoveToLine, OUTLINE_MOVE_TO_LINE_ACTION } from '../bulk/outline.bulk.move';
import { handleOutlineSetSchedule, OutlineRowPatcher, OUTLINE_SET_SCHEDULE_ACTION } from '../bulk/outline.bulk.schedule';
import { OutlineRelationConfig } from './outline.relation.config';
import { canDropOutlineRow } from './outline.relation.drag';

/** Labels for the two task-side bulk actions. Exported so a Detail command names its action identically. */
export const OUTLINE_SET_SCHEDULE_LABEL = 'Set schedule…';
export const OUTLINE_MOVE_TO_LINE_LABEL = 'Move to line…';
export const OUTLINE_SET_CREW_LABEL = 'Set crew…';

interface OutlineRevealStore {
  $gridApi(): OutlineRevealApi | null;
  setFocusedItemId(id: string): void;
}

const loadEntityBoard = (): Promise<Type<unknown>> => import('@workbuddy/ui/v2/blocks/entity.board').then(module => module.EntityBoardComponent);

/**
 * Every outline bulk command, built the one way that works from ⌘K.
 *
 * `resolveBulkPaletteSelection` drops a palette pick whose command carries no
 * `commandPalette.settingsPanel` config, so a command declared without this metadata is listed in
 * the palette and then silently refuses to run. These all execute outright once picked — their own
 * dialog does the asking — which is exactly `kind: 'action'`. Constructing them through one helper
 * is what keeps the next command from being added without it.
 */
export function outlineBulkCommand(id: string, label: string, iconLeft: string): Command {

  return new Command({ _id: id, label, iconLeft, data: { commandPalette: { settingsPanel: { kind: 'action' } } } });
}

export function createOutlineRelationBuilder<TRecord extends Entity>(config: OutlineRelationConfig<TRecord>): RelationBoardBuilder {

  const { kinds, features, commands, record: access, panel } = config;

  return args => {

    const dialogs = args.injector.get(EntityDialogs);
    const entityDataRaw = args.injector.get(EntityDataServiceRaw);
    const boardRef: { store: OutlineRowPatcher | null } = { store: null };
    const $outlineRefresh = signal(0);
    const boardInputs = computed<Readonly<Record<string, unknown>>>(() => {
      const record = (args.record() ?? {}) as TRecord;
      // `args.readonly` is page OR this board's enclosing tab/section — NOT `page.$readonly()`. A
      // schema `readonly` rule on the serviceLines tab (a Started plan, an archived template) never
      // flips the page signal, so reading the page alone left the board and every Detail the panel
      // mounts editable on a locked record. This is the signal the board passes down as
      // `context.readonly`, which is what the panel hands the Line / Task / Subtask Detail surface.
      const hostReadonly = args.readonly();
      const catalogueLocked = hostReadonly || access.isCatalogueLocked(record);
      const parent = { _id: record._id, name: record.name, type: access.parentEntity } as Entity;
      // ONE fetch feeds the whole tree. `loadTree` answers with every level for this record — line rows
      // and their task rows — so roots and children are slices of the same response, held here between
      // the grid's block requests. Asking the API again per expanded row is what produced duplicates:
      // a line-scoped read returns that line's root tasks as TOP-LEVEL rows with no `parentId`, so the
      // grid received the same roots a second time under the expanded line. Refetched on every root
      // load rather than cached across them, so `$outlineRefresh` and any save show new rows.
      let tree: OutlineRow[] = [];
      const loadTree = async (): Promise<OutlineRow[]> => {

        const response = await firstValueFrom(entityDataRaw.getAllV2(kinds.outline, {
          parentId: record._id,
          parentEntity: access.parentEntity,
          unpaged: true,
          isEmbedded: true,
          variant: 'tree',
        }));
        tree = response.items as OutlineRow[];
        return tree;
      };
      const childrenOf = (parentRowId: string | null): OutlineRow[] => tree.filter(row => (row.parentId ?? null) === parentRowId);
      const page = (rows: OutlineRow[], startRow: number, endRow: number) => ({ items: rows.slice(startRow, endRow), total: rows.length });
      const revealCreatedTask = async (row: OutlineRow, taskIds: string[], store: OutlineRevealStore): Promise<void> => {

        const taskId = taskIds[0];
        const api = store.$gridApi();
        if (!taskId || !api) return;
        await revealOutlineChild({ api, parentRowId: row.rowId, childRowId: `${kinds.taskPrefix}${taskId}`, setFocusedItemId: id => store.setFocusedItemId(id) });
      };
      const addTasksFromChecklist = config.addTasksFromChecklist
        ? (parent: EntityLink, onSaved?: (taskIds: string[]) => void | Promise<void>) => config.addTasksFromChecklist?.(args.injector, parent, async taskIds => {
          // `loadChildren` slices this closure's cached tree, so refresh it before asking AG Grid to
          // open the parent. Invalidating the entity query alone does not replace this array.
          await loadTree();
          await onSaved?.(taskIds);
        })
        : undefined;
      // Guarded rather than merely hidden by `hideAdd`: the same handler is reachable from the board
      // header's own command surfaces, so a locked catalogue must refuse the create, not just stop
      // drawing the button. Mirrors `openOutlineChildCreate`, which guards its own `readonly`.
      const createServiceLine = () => catalogueLocked ? undefined : void dialogs.open({
        entity: kinds.line,
        operation: 'create',
        params: access.createLineParams(record, parent),
        ...(access.createLineInitialValues ? { initialValues: access.createLineInitialValues(record, parent) } : {}),
        source: { entity: kinds.outline },
        afterSave: () => $outlineRefresh.update(version => version + 1),
      });
      const context: EntityBoardContext<OutlineRow> = {
        entity: kinds.outline,
        isEmbedded: true,
        // The panel's Line / Task / Subtask Detail inherits THIS signal, so it carries the catalogue
        // lock as well as the host lock: a Started plan's catalogue is not editable from the row's
        // Detail any more than it is from the board. Placement in a quickview is still not itself a
        // lock — an unlocked record opens its Detail editable exactly as before.
        readonly: catalogueLocked,
        panel: {
          kind: 'quickview',
          width: panel.width,
          minWidth: panel.minWidth,
          maxWidth: panel.maxWidth,
          resolveTarget: ({ rowId }) => {
            if (rowId.startsWith(kinds.taskPrefix)) return { entity: kinds.task, id: rowId.slice(kinds.taskPrefix.length) };
            if (rowId.startsWith(kinds.linePrefix)) return { entity: kinds.line, id: rowId.slice(kinds.linePrefix.length) };
            return null;
          },
        },
        header: {
          title: config.headerTitle,
          showTitle: true,
          parentId: record._id,
          parentEntity: access.parentEntity,
          hideAdd: catalogueLocked,
          onAdd: createServiceLine,
          showSearch: true,
          showBoardControls: false,
          showPaginator: false,
          showViewOptions: false,
          ...(config.variant ? { variant: config.variant } : {}),
        },
        restrictions: { supportedDisplays: [EntityViewDisplay.List] },
        empty: { title: config.emptyTitle },
        itemInteraction: catalogueLocked ? undefined : {
          // `N` on a focused row: a line takes a task, a root task takes a subtask, a subtask takes
          // nothing. Same dialog and the same reveal as the row's + button, so the two never drift.
          createChild: row => {

            if (!resolveOutlineCreateParent(row, kinds)) return null;
            const store = inject(EntityBoardStore);
            return () => openOutlineChildCreate({
              dialogs,
              kinds,
              row,
              readonly: catalogueLocked,
              afterSave: async result => {
                const taskId = (result as { _id?: string } | null)?._id;
                if (!taskId) return;
                await loadTree();
                await revealCreatedTask(row, [taskId], store);
              },
            });
          },
          ...(addTasksFromChecklist ? {
            createChildrenFromChecklist: (row: OutlineRow) => {

              const target = resolveOutlineCreateParent(row, kinds);
              if (!target?.parent) return null;
              const store = inject(EntityBoardStore);
              return () => addTasksFromChecklist(target.parent!, taskIds => revealCreatedTask(row, taskIds, store));
            },
          } : {}),
          selectable: {
            type: 'multiple',
            includeGroups: true,
            bulkCommands: ({ selectedItems }) => {

              boardRef.store = inject(EntityBoardStore);
              const shape = outlineBulkSelectionShape(selectedItems ?? [], kinds);
              if (!shape.lines && !shape.rootTasks && !shape.subtasks) return [];
              const bulk: Command[] = [];
              if (shape.rootTasks && features.schedule) bulk.push(outlineBulkCommand(OUTLINE_SET_SCHEDULE_ACTION, OUTLINE_SET_SCHEDULE_LABEL, 'fa-regular fa-calendar'));
              if (shape.rootTasks && features.moveToLine) bulk.push(outlineBulkCommand(OUTLINE_MOVE_TO_LINE_ACTION, OUTLINE_MOVE_TO_LINE_LABEL, 'fa-regular fa-arrow-right-to-bracket'));
              if (shape.lines && features.crew) bulk.push(outlineBulkCommand(OUTLINE_SET_CREW_ACTION, OUTLINE_SET_CREW_LABEL, 'fa-regular fa-helmet-safety'));
              if (shape.lines && features.blackout) bulk.push(outlineBulkCommand(OUTLINE_SET_BLACKOUT_ACTION, OUTLINE_BLACKOUT_LABEL, OUTLINE_BLACKOUT_ICON));
              bulk.push(outlineBulkCommand(OUTLINE_DELETE_ACTION, outlineDeleteLabel(shape), 'fa-regular fa-trash-can'));
              return bulk;
            },
          },
        },
        bulk: catalogueLocked ? undefined : {
          describeSelection: selectedItems => outlineSelectionSummary(outlineBulkSelectionShape(selectedItems, kinds)),
          onCommand: commandArgs => {

            const store = boardRef.store;
            if (!store) return { handled: true, clearSelection: false };
            // Every service is resolved LAZILY, after the action has matched an enabled feature. A
            // command the config turned off must fall through as unhandled without touching the
            // injector: the flag is what says this board never runs that action at all.
            const entityData = () => args.injector.get(EntityDataService);
            const toasts = () => args.injector.get(ToastService);
            const modalDialogs = () => args.injector.get(WorkBuddyDialogService);
            if (features.schedule && commandArgs.action === OUTLINE_SET_SCHEDULE_ACTION) return handleOutlineSetSchedule(commandArgs, { kinds, dialogs: modalDialogs(), entityData: entityData(), store, toasts: toasts(), today: args.injector.get(SCHEDULE_TODAY)(), workWeek: access.workWeek(record), timezone: access.timezone?.(record) });
            if (features.moveToLine && commandArgs.action === OUTLINE_MOVE_TO_LINE_ACTION) return handleOutlineMoveToLine(commandArgs, { kinds, reparentCommand: commands.reparent ?? '', dialogs: modalDialogs(), entityData: entityData(), store, toasts: toasts(), parentId: record._id });
            if (features.crew && commandArgs.action === OUTLINE_SET_CREW_ACTION) return handleOutlineSetCrew(commandArgs, { kinds, dialogs: modalDialogs(), entityData: entityData(), store, toasts: toasts() });
            if (features.blackout && commandArgs.action === OUTLINE_SET_BLACKOUT_ACTION) return handleOutlineSetBlackout(commandArgs, { kinds, dialogs: modalDialogs(), entityData: entityData(), entityDataRaw: args.injector.get(EntityDataServiceRaw), toasts: toasts() });
            if (commandArgs.action === OUTLINE_DELETE_ACTION) return handleOutlineDelete(commandArgs, { kinds, confirm: args.injector.get(ConfirmServicePro), entityData: entityData(), store, toasts: toasts() });
            return { handled: false };
          },
        },
        list: {
          className: 'service-plan-outline-tree',
          rowHeight: () => 40,
          displayProperties: {
            allow: config.displayProperties,
          },
          nested: {
            parentField: 'parentId',
            treeData: true,
            lazy: {
              pageSize: 100,
              refresh: $outlineRefresh,
              hasChildren: row => row.hasChildren,
              loadRoots: async ({ startRow, endRow }) => {

                await loadTree();
                return page(childrenOf(null), startRow, endRow);
              },
              loadChildren: async ({ parent: row, startRow, endRow }) => page(childrenOf(row.rowId), startRow, endRow),
              loadTree,
              filterTree: filterOutlineSearchRows,
            },
          },
          rowRenderer: OutlineRowRendererComponent,
        },
        reorder: catalogueLocked ? null : {
          field: 'sortOrder',
          tree: {
            canDrop: ({ item, over }) => canDropOutlineRow(item, over, kinds),
            onMove: async ({ item, over, after }) => {
              await args.injector.get(EntityDataService).executeCommand(kinds.outline, commands.move, {
                sourceEntity: item.sourceEntity,
                sourceId: item.sourceId,
                targetEntity: over.sourceEntity,
                targetId: over.sourceId,
                after,
              });
            },
          },
        },
        // `outlineEntityKinds` is how the shared row renderer learns which entity pair it is drawing:
        // AG Grid builds cell renderers from the grid's own injector, out of reach of this builder's.
        params: {
          parent,
          readonly: catalogueLocked,
          outlineEntityKinds: kinds,
          reloadOutline: loadTree,
          ...(features.noCrew ? { flagNoCrew: true } : {}),
          ...(addTasksFromChecklist ? { addTasksFromChecklist } : {}),
          ...optional('customer', access.customer?.(record)),
          ...optional('site', access.site?.(record)),
          ...optional('timezone', access.timezone?.(record)),
          ...optional('workingDays', access.workingDays?.(record)),
          ...optional('blackoutRanges', access.blackoutRanges?.(record)),
        },
        viewStateSlot: args.viewState,
      };
      return { context };
    });
    const inputs = computed<Readonly<Record<string, unknown>>>(() => ({ loader: loadEntityBoard, inputs: boardInputs() }));

    return { component: LazyRelationBoardHostComponent, inputs } satisfies RelationBoardMount;
  };
}

/** Keeps an absent accessor out of the params bag entirely rather than writing `undefined` into it. */
function optional(key: string, value: unknown): Record<string, unknown> {

  return value === undefined ? {} : { [key]: value };
}
